// CalendarView.js - Improved Space Utilization and Visual Design
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Badge
} from '@mui/material';
import { Visibility, ExpandMore, ExpandLess } from '@mui/icons-material';
import DetailedInfo from './DetailedInfo';
import { useTheme } from '@mui/material/styles';
import { diaryAPI, handleAPIError } from './api';

const CalendarView = ({ currentDate }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // State management
  const [groupsData, setGroupsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const [selectedDateGroups, setSelectedDateGroups] = useState([]);
  const [expandedDays, setExpandedDays] = useState(new Set());

  // Use the same successful approach as GroupsView
  const loadCalendarData = async () => {
    try {
      setLoading(true);
      setError(null);
  
      // Single API call - gets all meetings with group data already included
      const allMeetings = await diaryAPI.meetings.getAllMeetingsWithGroups({
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear()
      });
  
      // Transform to calendar format
      const meetingsMap = {};
      
      allMeetings.forEach(meeting => {
        const rawDate = meeting.meeting_date || meeting.previous_meeting_date;
        if (rawDate) {
          const meetingDate = new Date(rawDate);
          const day = meetingDate.getDate();
          
          if (!meetingsMap[day]) {
            meetingsMap[day] = [];
          }
  
          meetingsMap[day].push({
            groupId: meeting.group.id,
            groupName: meeting.group.name,
            meetingId: meeting.id,
            status: meeting.scheduled_status || 'scheduled',
            meetingStatus: meeting.meeting_status || 'pending',
            meetingDate: rawDate
          });
        }
      });
  
      setGroupsData(meetingsMap);
    } catch (err) {
      const errorMessage = handleAPIError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or currentDate changes
  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  // Toggle expanded state for a day
  const toggleDayExpansion = (day) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(day)) {
      newExpanded.delete(day);
    } else {
      newExpanded.add(day);
    }
    setExpandedDays(newExpanded);
  };

  // Company colors for different groups - with dark mode support
  const getGroupColor = (groupName) => {
    const colors = {
      'WASHINDI': { 
        bg: isDark ? '#1a2732' : '#e3f2fd', 
        color: isDark ? '#64b5f6' : '#1976d2', 
        border: isDark ? '#42a5f5' : '#1976d2' 
      },
      'Group B': { 
        bg: isDark ? '#2d1b33' : '#f3e5f5', 
        color: isDark ? '#ba68c8' : '#7b1fa2', 
        border: isDark ? '#ab47bc' : '#7b1fa2' 
      },
      'TEST123': { 
        bg: isDark ? '#1b2e1b' : '#e8f5e8', 
        color: isDark ? '#81c784' : '#388e3c', 
        border: isDark ? '#66bb6a' : '#388e3c' 
      },
      'Cash Collection': { 
        bg: isDark ? '#332514' : '#fff3e0', 
        color: isDark ? '#ffb74d' : '#f57c00', 
        border: isDark ? '#ffa726' : '#f57c00' 
      },
      'Happy Mothers': { 
        bg: isDark ? '#331a24' : '#fce4ec', 
        color: isDark ? '#f06292' : '#c2185b', 
        border: isDark ? '#ec407a' : '#c2185b' 
      },
      'ngurueni': { 
        bg: isDark ? '#232e1b' : '#f1f8e9', 
        color: isDark ? '#aed581' : '#689f38', 
        border: isDark ? '#9ccc65' : '#689f38' 
      },
      'Group4': { 
        bg: isDark ? '#33291a' : '#fff8e1', 
        color: isDark ? '#ffd54f' : '#ffa000', 
        border: isDark ? '#ffca28' : '#ffa000' 
      },
      'JAMII BORA': { 
        bg: isDark ? '#232938' : '#e8eaf6', 
        color: isDark ? '#7986cb' : '#3f51b5', 
        border: isDark ? '#5c6bc0' : '#3f51b5' 
      },
      'UMOJA KIRERE': { 
        bg: isDark ? '#331a1a' : '#ffebee', 
        color: isDark ? '#e57373' : '#e53935', 
        border: isDark ? '#ef5350' : '#e53935' 
      },
      'MARIA-INI': { 
        bg: isDark ? '#2e1b33' : '#f3e5f5', 
        color: isDark ? '#ba68c8' : '#8e24aa', 
        border: isDark ? '#ab47bc' : '#8e24aa' 
      },
      'KIANJUGU': { 
        bg: isDark ? '#1a2e2b' : '#e0f2f1', 
        color: isDark ? '#4db6ac' : '#00695c', 
        border: isDark ? '#26a69a' : '#00695c' 
      },
      'BIDII KARIARA': { 
        bg: isDark ? '#1b2e1b' : '#e8f5e8', 
        color: isDark ? '#66bb6a' : '#2e7d32', 
        border: isDark ? '#4caf50' : '#2e7d32' 
      },
      'INUKA KARURI': { 
        bg: isDark ? '#332314' : '#fff3e0', 
        color: isDark ? '#ff9800' : '#ef6c00', 
        border: isDark ? '#fb8c00' : '#ef6c00' 
      },
      'IMANI WINNERS': { 
        bg: isDark ? '#252e1b' : '#f1f8e9', 
        color: isDark ? '#9ccc65' : '#558b2f', 
        border: isDark ? '#8bc34a' : '#558b2f' 
      }
    };
    
    // Generate a color based on group name hash if not in predefined colors
    if (!colors[groupName]) {
      const hash = groupName.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const hue = Math.abs(hash) % 360;
      return {
        bg: isDark ? `hsl(${hue}, 35%, 20%)` : `hsl(${hue}, 65%, 95%)`,
        color: isDark ? `hsl(${hue}, 55%, 65%)` : `hsl(${hue}, 65%, 40%)`,
        border: isDark ? `hsl(${hue}, 55%, 55%)` : `hsl(${hue}, 65%, 50%)`
      };
    }
    
    return colors[groupName];
  };

  // Get calendar data for the current month
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of the month and how many days in the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get what day of week the month starts (0 = Sunday, 1 = Monday, etc.)
    const startDay = firstDay.getDay();
    const startMonday = startDay === 0 ? 6 : startDay - 1; // Convert to Monday = 0
    
    // Create calendar grid
    const weeks = [];
    let currentWeek = [];
    let dayCount = 1;
    
    // Fill first week with empty cells and start adding days
    for (let i = 0; i < 7; i++) {
      if (i < startMonday) {
        currentWeek.push(null);
      } else if (dayCount <= daysInMonth) {
        currentWeek.push(dayCount);
        dayCount++;
      } else {
        currentWeek.push(null);
      }
    }
    weeks.push(currentWeek);
    
    // Fill remaining weeks
    while (dayCount <= daysInMonth) {
      currentWeek = [];
      for (let i = 0; i < 7; i++) {
        if (dayCount <= daysInMonth) {
          currentWeek.push(dayCount);
          dayCount++;
        } else {
          currentWeek.push(null);
        }
      }
      weeks.push(currentWeek);
    }
    
    return weeks;
  }, [currentDate]);

  // Handle view button click
  const handleViewDay = (day) => {
    const dayGroups = groupsData[day] || [];
    const groupIds = dayGroups.map(group => group.groupId);
    setSelectedDateGroups(groupIds);
    setShowDetailedInfo(true);
  };

  // Handle back from detailed info
  const handleBackToCalendar = () => {
    setShowDetailedInfo(false);
    setSelectedDateGroups([]);
  };

  // Get today's date for highlighting
  const today = new Date();
  const isToday = (day) => {
    return day && 
           today.getDate() === day && 
           today.getMonth() === currentDate.getMonth() && 
           today.getFullYear() === currentDate.getFullYear();
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayLabels = ['(4 days)', '(4 days)', '(4 days)', '(5 days)', '(5 days)', '', ''];

  // If showing detailed info, render DetailedInfo component
  if (showDetailedInfo) {
    return (
      <DetailedInfo 
        onBack={handleBackToCalendar} 
        selectedGroupId={null}
        selectedGroupIds={selectedDateGroups} // Pass multiple group IDs
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        p: 3
      }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading calendar data...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load calendar data: {error}
        </Alert>
        <Button variant="contained" onClick={loadCalendarData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
          {/* Header Row */}
          <TableHead>
            <TableRow>
              {/* Week Column */}
              <TableCell
                sx={{
                  backgroundColor: isDark ? '#2b323c' : '#f5f5f5',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  width: '80px',
                  py: 2,
                }}
              >
                Week
              </TableCell>
              
              {/* Day Columns - Show only Monday to Friday by default */}
              {dayNames.slice(0, 5).map((day, index) => (
                <TableCell
                key={day}
                sx={{
                  backgroundColor: isDark ? '#2b323c' : '#f5f5f5',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  width: '20%',
                  py: 2,
                }}
              >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {day}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666' }}>
                      {dayLabels[index]}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Calendar Body */}
          <TableBody>
            {calendarData.map((week, weekIndex) => (
              <TableRow key={weekIndex}>
                {/* Week Label */}
                <TableCell
                sx={{
                  backgroundColor: isDark ? '#1f262e' : '#fafafa',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  verticalAlign: 'top',
                }}
              >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Week
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {weekIndex + 1}
                  </Typography>
                </TableCell>

                {/* Day Cells - Show only Monday to Friday */}
                {week.slice(0, 5).map((day, dayIndex) => (
                  <TableCell
                  key={dayIndex}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    minHeight: '160px',
                    verticalAlign: 'top',
                    p: 1,
                    position: 'relative',
                    backgroundColor: day 
                    ? (isToday(day) 
                        ? (isDark ? '#4a3800' : '#fff3e0')
                        : (isDark ? '#2b323c' : 'white'))
                    : (isDark ? '#1f262e' : '#f9f9f9'),
                  }}
                >
                    {day && (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        {/* Day Header - Optimized Layout */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1,
                            minHeight: '24px'
                          }}
                        >
                          {/* Day Number with Today Indicator */}
                          <Badge
                            badgeContent={isToday(day) ? 'Today' : null}
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '8px',
                                height: '16px',
                                minWidth: '16px',
                                right: -8,
                                top: -4
                              }
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: isToday(day) ? '#f57c00' : (isDark ? '#e0e0e0' : '#333'),
                                fontSize: '18px'
                              }}
                            >
                              {day}
                            </Typography>
                          </Badge>

                          {/* Meeting Count Indicator */}
                          {groupsData[day] && groupsData[day].length > 0 && (
                            <Chip
                              label={groupsData[day].length}
                              size="small"
                              sx={{
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 600,
                                height: '18px',
                                minWidth: '18px',
                                '& .MuiChip-label': {
                                  px: 0.5,
                                },
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Groups Container - Improved Layout */}
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.3,
                            mb: 1,
                            overflow: 'hidden',
                          }}
                        >
                          {groupsData[day] && (() => {
                            const dayGroups = groupsData[day];
                            const isExpanded = expandedDays.has(day);
                            const displayLimit = 4;
                            const groupsToShow = isExpanded ? dayGroups : dayGroups.slice(0, displayLimit);
                            const hasMore = dayGroups.length > displayLimit;

                            return (
                              <>
                                {groupsToShow.map((group, index) => {
                                  const colors = getGroupColor(group.groupName);
                                  return (
                                    <Box
                                      key={index}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: colors.bg,
                                        borderLeft: `3px solid ${colors.border}`,
                                        borderRadius: '4px',
                                        px: 1,
                                        py: 0.3,
                                        fontSize: '10px',
                                        minHeight: '22px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          backgroundColor: colors.color,
                                          color: 'white',
                                          '& .MuiTypography-root': {
                                            color: 'white'
                                          }
                                        }
                                      }}
                                      onClick={() => handleViewDay(day)}
                                    >
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          color: colors.color,
                                          fontWeight: 500,
                                          fontSize: '10px',
                                          lineHeight: 1.2,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flex: 1
                                        }}
                                      >
                                        {group.groupName}
                                      </Typography>
                                      
                                      {/* Status dot */}
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: group.status === 'scheduled' ? '#4caf50' : '#f44336',
                                          ml: 0.5,
                                          flexShrink: 0
                                        }}
                                      />
                                    </Box>
                                  );
                                })}
                                
                                {/* Show More/Less Button */}
                                {hasMore && (
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDayExpansion(day);
                                    }}
                                    startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    sx={{
                                      fontSize: '9px',
                                      textTransform: 'none',
                                      minHeight: '20px',
                                      color: isDark ? '#999' : '#666',
                                      '&:hover': {
                                        backgroundColor: isDark ? '#3a4149' : '#f0f0f0',
                                      },
                                      '& .MuiButton-startIcon': {
                                        marginRight: '2px',
                                        '& > *:nth-of-type(1)': {
                                          fontSize: '12px'
                                        }
                                      }
                                    }}
                                  >
                                    {isExpanded ? 'Show Less' : `+${dayGroups.length - displayLimit} More`}
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        </Box>

                        {/* View All Button - Compact Design */}
                        {groupsData[day] && groupsData[day].length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility sx={{ fontSize: '12px' }} />}
                            onClick={() => handleViewDay(day)}
                            sx={{
                              fontSize: '9px',
                              textTransform: 'none',
                              height: '22px',
                              minWidth: '70px',
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              borderWidth: '1px',
                              '&:hover': {
                                backgroundColor: isDark ? '#1a2732' : '#e3f2fd',
                                borderColor: '#1565c0',
                              },
                              '& .MuiButton-startIcon': {
                                marginRight: '4px'
                              }
                            }}
                          >
                            View All
                          </Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Information */}
      <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: isDark ? '#2b323c' : '#f8f9fa',
          borderRadius: 1 
        }}>
        <Typography variant="body2" color="text.secondary">
          {Object.values(groupsData).reduce((total, dayGroups) => total + dayGroups.length, 0)} total meetings scheduled for {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
      </Box>
    </Box>
  );
};

export default CalendarView;